import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

class PanelTrackingStore {
  // Observables
  isLoading = false;
  error: string | null = null;
  filters = {
    jobNumber: "",
    controlNumber: "",
    status: "",
    productType: "",
  };
  selectedPanelIds: string[] = [];

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  updateFilter(field: string, value: any) {
    this.filters = { ...this.filters, [field]: value };
  }

  togglePanelSelection(panelId: string) {
    const index = this.selectedPanelIds.indexOf(panelId);
    if (index > -1) {
      this.selectedPanelIds.splice(index, 1);
    } else {
      this.selectedPanelIds.push(panelId);
    }
  }

  clearSelection() {
    this.selectedPanelIds = [];
  }

  // Async actions
  async loadPanels() {
    this.setLoading(true);
    this.setError(null);

    try {
      await (this.rootStore.panelStore as ServiceStore).find({
        query: {
          $limit: 1000,
        },
      });
    } catch (error) {
      console.error("Error loading panels:", error);
      runInAction(() => {
        this.setError("Failed to load panels");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  async generateQRCode(panelId: string) {
    try {
      // Implementation for QR code generation
      console.log(`Generating QR code for panel: ${panelId}`);
      // This would typically call an API to generate and download QR code
      const qrData = {
        panelId,
        url: `${window.location.origin}/field-reporting/panel/${panelId}`,
      };

      // Placeholder for actual QR generation logic
      alert(
        `QR Code generated for Panel: ${panelId}\nData: ${JSON.stringify(
          qrData
        )}`
      );
    } catch (error) {
      console.error("Error generating QR code:", error);
      runInAction(() => {
        this.setError("Failed to generate QR code");
      });
    }
  }

  async generateBulkQRCodes() {
    if (this.selectedPanelIds.length === 0) {
      return;
    }

    try {
      console.log(
        `Generating QR codes for ${this.selectedPanelIds.length} panels`
      );
      // Implementation for bulk QR code generation
      for (const panelId of this.selectedPanelIds) {
        await this.generateQRCode(panelId);
      }
      runInAction(() => {
        this.clearSelection();
      });
    } catch (error) {
      console.error("Error generating bulk QR codes:", error);
      runInAction(() => {
        this.setError("Failed to generate bulk QR codes");
      });
    }
  }

  async exportPanelData() {
    try {
      const panels = this.filteredPanels;
      const csvContent = this.convertToCSV(panels);
      this.downloadCSV(csvContent, "panel_tracking.csv");
    } catch (error) {
      console.error("Error exporting panel data:", error);
      runInAction(() => {
        this.setError("Failed to export panel data");
      });
    }
  }

  async importPanelData() {
    try {
      // Implementation for panel data import
      console.log("Opening import dialog...");
      // This would typically open a file picker and process the imported data
    } catch (error) {
      console.error("Error importing panel data:", error);
      runInAction(() => {
        this.setError("Failed to import panel data");
      });
    }
  }

  // Helper methods
  get filteredPanels() {
    let panels = (this.rootStore.panelStore as ServiceStore).entities;
    console.log("panels", panels);

    if (this.filters.jobNumber) {
      panels = panels.filter((p: any) =>
        p.jobNumber?.toString().includes(this.filters.jobNumber)
      );
    }

    if (this.filters.controlNumber) {
      panels = panels.filter((p: any) =>
        p.controlNumber
          ?.toLowerCase()
          .includes(this.filters.controlNumber.toLowerCase())
      );
    }

    if (this.filters.productType) {
      panels = panels.filter((p: any) =>
        p.productType
          ?.toLowerCase()
          .includes(this.filters.productType.toLowerCase())
      );
    }

    if (this.filters.status) {
      panels = panels.filter((p: any) => {
        switch (this.filters.status) {
          case "installed":
            return p.installationDate && p.installationDate !== "1970-01-01";
          case "aligned":
            return (
              p.alignmentDate &&
              p.alignmentDate !== "1970-01-01" &&
              (!p.installationDate || p.installationDate === "1970-01-01")
            );
          case "welded":
            return (
              p.weldedDate &&
              p.weldedDate !== "1970-01-01" &&
              (!p.alignmentDate || p.alignmentDate === "1970-01-01")
            );
          case "cast":
            return (
              p.castingDate &&
              p.castingDate !== "1970-01-01" &&
              (!p.weldedDate || p.weldedDate === "1970-01-01")
            );
          case "planned":
            return !p.castingDate || p.castingDate === "1970-01-01";
          default:
            return true;
        }
      });
    }

    return panels;
  }

  convertToCSV(data: any[]) {
    if (data.length === 0) return "";

    const headers = [
      "Panel ID",
      "Mark #",
      "Control #",
      "Job #",
      "Product Type",
      "Elevation",
      "Install Seq",
      "Cast Date",
      "Install Date",
      "Mold #",
      "Table #",
      "Finish",
      "Sq Ft",
      "Weight",
    ];

    const rows = data.map((panel) => [
      panel.panelId || "",
      panel.markNumber || "",
      panel.controlNumber || "",
      panel.jobNumber || "",
      panel.productType || "",
      panel.elevation || "",
      panel.installationSequence || "",
      panel.castingDate || "",
      panel.installationDate || "",
      panel.moldNumber || "",
      panel.tableNumber || "",
      panel.finishType || "",
      panel.totalSqFt || "",
      panel.individualPanelWeight || "",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    return csvContent;
  }

  downloadCSV(content: string, filename: string) {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Computed values
  get totalPanels() {
    return (this.rootStore.panelStore as ServiceStore).entities.length;
  }

  get filteredPanelCount() {
    return this.filteredPanels.length;
  }
}

export default PanelTrackingStore;
